import cn from "classnames"
import { useAppSelector } from "integrations/redux/hooks"
import { selectWeatherPreferences } from "features/map/control/selectors"
import { WindUnit } from "features/map/control/types"
import { WIND_HEATMAP_COLOR_STOPS, WIND_HEATMAP_MAX_SPEED_MS } from "./WindParticleLayer"

const cssRgba = (c: readonly [number, number, number], alpha: number): string =>
    `rgba(${Math.round(c[0] * 255)}, ${Math.round(c[1] * 255)}, ${Math.round(c[2] * 255)}, ${alpha})`

const cssRgb = (c: readonly [number, number, number]): string => cssRgba(c, 1)

const AXIS_MIN_MS = 0
const AXIS_MAX_MS = WIND_HEATMAP_MAX_SPEED_MS
const AXIS_RANGE_MS = AXIS_MAX_MS - AXIS_MIN_MS

const pctOf = (speedMs: number): string =>
    (((speedMs - AXIS_MIN_MS) / AXIS_RANGE_MS) * 100).toFixed(2)

const gradientCss = WIND_HEATMAP_COLOR_STOPS.map(
    (s) => `${cssRgb(s.color)} ${pctOf(s.value)}%`
).join(", ")

const BASE_TICKS_MS = [0, 5, 10, 15, 20, 25, 30] as const

const UNIT_CONVERTERS: Record<WindUnit, (ms: number) => number> = {
    [WindUnit.MetersPerSecond]: (v) => v,
    [WindUnit.KilometersPerHour]: (v) => v * 3.6,
    [WindUnit.MilesPerHour]: (v) => v * 2.2369362920544,
    [WindUnit.Knots]: (v) => v * 1.9438444924406,
}

export const WindHeatmapLegend = ({ isLight }: { isLight: boolean }) => {
    const { windUnit } = useAppSelector(selectWeatherPreferences)
    const ticks = BASE_TICKS_MS.map((ms) => Math.round(UNIT_CONVERTERS[windUnit](ms)))
    return (
        <div className="flex items-end gap-2">
            <div>
                <div
                    className={cn("h-2 w-[230px] rounded border", {
                        ["bg-gray-300 border-black-500"]: isLight,
                        ["bg-gray-600 border-white-500"]: !isLight,
                    })}
                    style={{ background: `linear-gradient(to right, ${gradientCss})` }}
                />
                <div className="mt-1 flex h-3 w-[230px] justify-between">
                    {ticks.map((s, i) => (
                        <span
                            key={i}
                            className={cn("font-mono text-[10px] leading-none", {
                                ["text-white"]: !isLight,
                                ["text-black"]: isLight,
                            })}
                        >
                            {s}
                        </span>
                    ))}
                </div>
            </div>
            <span
                className={cn("text-xs leading-none", {
                    ["text-white"]: !isLight,
                    ["text-black"]: isLight,
                })}
            >
                {windUnit}
            </span>
        </div>
    )
}
